import { useState } from "react";
import { blankProfessionProfile } from "@guildmaster/core/professions";
import {
  commandSchema,
  type Actor,
  type Command,
  type GuildRecord,
  type RecordValue,
} from "@guildmaster/core/platform";

type Field = {
  key: string;
  label: string;
  type?: "textarea" | "datetime-local" | "number" | "checkbox" | "time";
  options?: string[];
  help?: string;
};
const fields: Record<RecordValue["kind"], Field[]> = {
  professions: [],
  availability: [
    {
      key: "timezone",
      label: "Timezone",
      help: "For example, Australia/Sydney",
    },
    {
      key: "days",
      label: "Available days",
      help: "Comma-separated numbers: Sunday 0, Monday 1, through Saturday 6",
    },
    { key: "startTime", label: "From", type: "time" },
    { key: "endTime", label: "Until", type: "time" },
    { key: "effectiveAt", label: "Effective from", type: "datetime-local" },
    {
      key: "reviewAt",
      label: "Review this availability",
      type: "datetime-local",
    },
    { key: "notes", label: "Exceptions and notes", type: "textarea" },
  ],
  raid: [
    { key: "title", label: "Raid title" },
    { key: "startsAt", label: "Starts", type: "datetime-local" },
    { key: "endsAt", label: "Ends", type: "datetime-local" },
    { key: "timezone", label: "Timezone" },
    { key: "zone", label: "Raid / zone" },
    { key: "difficulty", label: "Difficulty" },
    { key: "size", label: "Raid size", type: "number" },
    {
      key: "status",
      label: "Status",
      options: ["draft", "confirmed", "cancelled"],
    },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  recruitment: [
    { key: "characterName", label: "Character or intended character" },
    { key: "desiredRole", label: "Desired class and role" },
    { key: "availability", label: "Availability", type: "textarea" },
    {
      key: "stage",
      label: "Application stage",
      options: [
        "applied",
        "review",
        "trial",
        "accepted",
        "declined",
        "withdrawn",
      ],
    },
    { key: "reason", label: "Application / decision notes", type: "textarea" },
  ],
  reference: [
    { key: "title", label: "Title" },
    { key: "body", label: "Guild guidance", type: "textarea" },
    {
      key: "status",
      label: "Publication status",
      options: ["draft", "approved", "retired"],
    },
    { key: "effectiveAt", label: "Effective from", type: "datetime-local" },
  ],
  communication: [
    {
      key: "channelId",
      label: "Destination Discord channel ID",
      help: "Copy the channel ID from Discord. Access is checked before sending.",
    },
    { key: "content", label: "Message preview", type: "textarea" },
    {
      key: "mentions",
      label: "Explicit user mention IDs",
      help: "Optional comma-separated user IDs. No automatic everyone, role or reply mentions.",
    },
  ],
  correction: [
    { key: "recordId", label: "Record reference" },
    { key: "description", label: "What needs correcting?", type: "textarea" },
    {
      key: "status",
      label: "Status",
      options: ["open", "resolved", "declined"],
    },
    { key: "resolution", label: "Officer resolution", type: "textarea" },
  ],
  character: [
    { key: "className", label: "Class", options: ["Druid", "Hunter", "Mage", "Paladin", "Priest", "Rogue", "Shaman", "Warlock", "Warrior"] },
    { key: "reviewReason", label: "Officer decision reason", type: "textarea" },
    { key: "name", label: "Exact character name" },
    { key: "realm", label: "Realm" },
    { key: "region", label: "Region", options: ["us", "eu", "kr", "tw"] },
    {
      key: "status",
      label: "Identity status",
      options: ["proposed", "verified", "rejected", "retired"],
    },
  ],
  declaration: [
    { key: "characterId", label: "Verified character" },
    { key: "mainOrAlt", label: "Main or alt", options: ["main", "alt"] },
    { key: "mainSpec", label: "Declared main spec" },
    { key: "offSpec", label: "Declared offspec (optional)" },
    {
      key: "willingRoles",
      label: "Roles you can cover",
      help: "Comma-separated tank, healer, melee or ranged",
    },
    { key: "effectiveAt", label: "Effective from", type: "datetime-local" },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  assignment: [
    { key: "raidId", label: "Confirmed raid" },
    { key: "characterId", label: "Verified character" },
    {
      key: "role",
      label: "Assigned role",
      options: ["tank", "healer", "melee", "ranged", "bench"],
    },
    { key: "spec", label: "Assigned spec" },
    {
      key: "status",
      label: "Status",
      options: ["proposed", "confirmed", "cancelled"],
    },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  attendance: [
    { key: "raidId", label: "Confirmed raid" },
    { key: "characterId", label: "Verified character" },
    {
      key: "expectation",
      label: "Attendance expectation",
      options: ["unknown", "expected", "not-expected"],
    },
    {
      key: "state",
      label: "Observed attendance",
      options: ["unknown", "present", "absent", "bench", "excused", "partial"],
    },
    { key: "confirmed", label: "Officer-confirmed evidence", type: "checkbox" },
    {
      key: "evidenceReference",
      label: "Evidence reference",
      help: "A source report, signed roster or explicit officer record",
    },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  preferences: [
    { key: "characterId", label: "Verified character" },
    {
      key: "notes",
      label: "Loot preferences",
      type: "textarea",
      help: "Preferences do not create claims, awards or priority.",
    },
  ],
};
function defaults(
  kind: RecordValue["kind"],
  context: GuildRecord["contextId"],
): Record<string, unknown> {
  const now = Date.now(),
    shared = { kind, effectiveAt: now, notes: "" };
  switch (kind) {
    case "professions": return blankProfessionProfile();
    case "availability":
      return {
        ...shared,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        days: [],
        startTime: "19:00",
        endTime: "23:00",
        reviewAt: now + 30 * 86400000,
      };
    case "raid":
      return {
        kind,
        title: "",
        startsAt: now + 86400000,
        endsAt: now + 86400000 + 3 * 3600000,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        raidHelperEventId: null,
        source: "manual",
        zone: "",
        difficulty: "",
        size: 25,
        status: "draft",
        notes: "",
      };
    case "recruitment":
      return {
        kind,
        characterName: "",
        desiredRole: "",
        availability: "",
        stage: "applied",
        reason: "",
        effectiveAt: now,
      };
    case "reference":
      return { kind, title: "", body: "", status: "draft", effectiveAt: now };
    case "communication":
      return {
        kind,
        channelId: "",
        content: "",
        mentions: [],
        status: "draft",
        effectiveAt: now,
      };
    case "correction":
      return {
        kind,
        recordId: "",
        description: "",
        status: "open",
        resolution: "",
        effectiveAt: now,
      };
    case "character":
      return {
        kind,
        name: "",
        realm: context === "forever-behind" ? null : "",
        region: context === "forever-behind" ? null : "us",
        gameVersion: context === "forever-behind" ? "forever" : "classic",
        memberId: null,
        legacyCharacterId: null,
        status: "proposed",
        evidence: {
          source: "member-declaration",
          reference: "Website character proposal",
          observedAt: now,
        },
        effectiveAt: now,
      };
    case "declaration":
      return {
        ...shared,
        characterId: "",
        mainOrAlt: "main",
        mainSpec: "",
        offSpec: null,
        willingRoles: [],
      };
    case "assignment":
      return {
        ...shared,
        raidId: "",
        raidRevision: 1,
        characterId: "",
        role: "melee",
        spec: "",
        status: "proposed",
        declarationId: "",
        declarationRevision: 1,
        acknowledgement: null,
      };
    case "attendance":
      return {
        ...shared,
        raidId: "",
        characterId: "",
        expectation: "unknown",
        state: "unknown",
        evidence: [],
        confirmed: false,
      };
    case "preferences":
      return { ...shared, characterId: "", items: [] };
  }
}
export function RecordEditor({
  kind,
  current,
  actor,
  contextId,
  records,
  busy,
  onSubmit,
  onCancel,
}: {
  kind: RecordValue["kind"];
  current?: GuildRecord;
  actor: Actor;
  contextId: GuildRecord["contextId"];
  records: GuildRecord[];
  busy: boolean;
  onSubmit: (command: Command) => void;
  onCancel: () => void;
}) {
  // Remount the inner form when selecting a different record or kind.
  if (kind === "professions") return <p className="notice">Manage this entry in <a href="/professions?view=mine">Professions</a>. <button className="text-button" onClick={onCancel}>Close</button></p>;
  return (
    <Editor
      key={kind + ":" + (current?.id ?? "new") + ":" + (current?.revision ?? 0)}
      {...{
        kind,
        current,
        actor,
        contextId,
        records,
        busy,
        onSubmit,
        onCancel,
      }}
    />
  );
}
function Editor({
  kind,
  current,
  actor,
  contextId,
  records,
  busy,
  onSubmit,
  onCancel,
}: Parameters<typeof RecordEditor>[0]) {
  const [values, setValues] = useState<Record<string, unknown>>(
    current ? { ...current.value } : defaults(kind, contextId),
  );
  const [error, setError] = useState("");
  const officer = actor.capabilities.includes("approve");
  function save(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    const draft = { ...values };
    if (kind === "assignment") {
      const declaration = records.find(
        (record) =>
          record.value.kind === "declaration" &&
          record.value.characterId === draft.characterId,
      );
      draft.raidRevision = records.find(
        (record) => record.id === draft.raidId,
      )?.revision;
      draft.declarationId = declaration?.id;
      draft.declarationRevision = declaration?.revision;
    }
    if (kind === "attendance") {
      draft.evidence = draft.evidenceReference
        ? [
            {
              source: "officer-record",
              reference: String(draft.evidenceReference),
              observedAt: Date.now(),
            },
          ]
        : draft.evidence;
      delete draft.evidenceReference;
    }
    if (draft.offSpec === "") draft.offSpec = null;
    const selected =
      typeof draft.characterId === "string"
        ? records.find((record) => record.id === draft.characterId)
        : undefined;
    const command = commandSchema.safeParse({
      type: "record.put",
      contextId,
      id: current?.id ?? crypto.randomUUID(),
      ownerId: current?.ownerId ?? selected?.ownerId ?? actor.userId,
      expectedRevision: current?.revision ?? 0,
      value: draft,
    });
    if (!command.success) {
      setError(
        command.error.issues
          .map((issue) => issue.path.slice(1).join(" ") + ": " + issue.message)
          .join(". "),
      );
      return;
    }
    onSubmit(command.data);
  }
  return (
    <form className="panel editor" onSubmit={save}>
      <h2>
        {current ? "Review " : "New "}
        {kind}
      </h2>
      <p className="fine">
        {current
          ? "Saving creates revision " +
            (current.revision + 1) +
            ". Earlier revisions remain in the history."
          : "The record is saved after validation. Track progress in your requests."}
      </p>
      <div className="form-grid">
        {fields[kind]
          .filter(
            (field) =>
              !(
                kind === "character" &&
                contextId === "forever-behind" &&
                ["realm", "region"].includes(field.key)
              ),
          )
          .map((field) => {
            const linked =
              field.key === "characterId"
                ? records.filter(
                    (record) =>
                      record.value.kind === "character" &&
                      record.value.status === "verified",
                  )
                : field.key === "raidId"
                  ? records.filter(
                      (record) =>
                        record.value.kind === "raid" &&
                        record.value.status === "confirmed",
                    )
                  : null;
            const options = field.options?.filter(
              (option) =>
                officer ||
                (kind === "recruitment" && field.key === "stage"
                  ? ["applied", "withdrawn"].includes(option)
                  : kind === "correction" && field.key === "status"
                    ? option === "open"
                    : true),
            );
            const raw = values[field.key],
              isArray = ["days", "mentions", "willingRoles"].includes(
                field.key,
              );
            const displayed =
              field.type === "datetime-local" && typeof raw === "number"
                ? new Date(raw - new Date(raw).getTimezoneOffset() * 60000)
                    .toISOString()
                    .slice(0, 16)
                : Array.isArray(raw)
                  ? raw.join(", ")
                  : String(raw ?? "");
            const update = (value: string | boolean) =>
              setValues((existing) => ({
                ...existing,
                [field.key]:
                  field.type === "datetime-local"
                    ? new Date(String(value)).getTime()
                    : field.type === "number"
                      ? Number(value)
                      : isArray
                        ? String(value)
                            .split(",")
                            .map((item) => item.trim())
                            .filter(Boolean)
                            .map((item) =>
                              field.key === "days" ? Number(item) : item,
                            )
                        : value,
              }));
            return (
              <label
                key={field.key}
                className={field.type === "textarea" ? "wide" : ""}
              >
                {field.label}
                {linked ? (
                  <select
                    value={displayed}
                    onChange={(e) => update(e.target.value)}
                    required
                  >
                    <option value="">Select a verified record</option>
                    {linked.map((record) => (
                      <option value={record.id} key={record.id}>
                        {"name" in record.value
                          ? record.value.name
                          : "title" in record.value
                            ? record.value.title
                            : record.id}
                      </option>
                    ))}
                  </select>
                ) : options ? (
                  <select
                    value={displayed}
                    onChange={(e) => update(e.target.value)}
                  >
                    {options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : field.type === "textarea" ? (
                  <textarea
                    rows={5}
                    maxLength={kind === "communication" ? 1900 : 6000}
                    value={displayed}
                    onChange={(e) => update(e.target.value)}
                  />
                ) : field.type === "checkbox" ? (
                  <input
                    type="checkbox"
                    checked={Boolean(raw)}
                    onChange={(e) => update(e.target.checked)}
                  />
                ) : (
                  <input
                    type={field.type ?? "text"}
                    value={displayed}
                    onChange={(e) => update(e.target.value)}
                    maxLength={1500}
                  />
                )}
                {field.help && <small>{field.help}</small>}
              </label>
            );
          })}
      </div>
      {error && (
        <p role="alert" className="notice error">
          {error}
        </p>
      )}
      <div className="form-actions">
        <button className="button primary" disabled={busy}>
          {busy ? "Submitting…" : "Save revision"}
        </button>
        <button type="button" className="text-button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
